/**
 * What was on screen when the person last spoke (RFC 0304 3d).
 *
 * THE LATEST PERSON MESSAGE, and only it. A selection is sticky because a
 * person who points once keeps meaning it; a view is not, because the window
 * moves between messages and "this" means what was on screen when they sent.
 * A newer message sent from a bare thread has no view, and then there is none:
 * reaching back to an older one would describe a window that is gone.
 *
 * Messages from agents and events are skipped rather than ending the search. A
 * turn's own reply comes after the person's message it answers, and the view
 * belongs to that message until the person speaks again.
 */

import { topoOrder } from './projection-order'
import type { LogDoc, MessageView, ViewPane } from './types'

const KINDS: ReadonlySet<ViewPane['kind']> = new Set([
  'document',
  'session',
  'session-list',
  'terminal',
  'empty',
])

/** Parse a stamped view, or null. A malformed stamp is no view, never a throw:
 *  it is read on the way to serving a turn. */
export function parseMessageView(json: unknown): MessageView | null {
  if (json == null) return null
  let raw: unknown
  try {
    raw = JSON.parse(String(json))
  } catch {
    return null
  }
  if (!raw || typeof raw !== 'object') return null
  const r = raw as { panes?: unknown; space?: unknown }
  if (!Array.isArray(r.panes)) return null
  const panes: ViewPane[] = []
  for (const p of r.panes) {
    if (!p || typeof p !== 'object') continue
    const q = p as Record<string, unknown>
    const kind = String(q.kind) as ViewPane['kind']
    if (!KINDS.has(kind)) continue
    panes.push({
      kind,
      ...(q.id ? { id: String(q.id) } : {}),
      ...(q.title ? { title: String(q.title) } : {}),
      focused: q.focused === true,
    })
  }
  if (panes.length === 0) return null
  return { panes, ...(r.space ? { space: String(r.space) } : {}) }
}

export function projectView(doc: LogDoc): MessageView | null {
  let latest: unknown = null
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'message') continue
    const m = p.payload as { role?: unknown; viewJson?: unknown }
    if (String(m.role) !== 'user') continue
    latest = m.viewJson ?? null
  }
  return parseMessageView(latest)
}
